import { Fragment, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

import { useModal } from "../../contexts/ModalContext";
import { programsApi } from "../../services/api/program.api";
import { userCoursesApi } from "../../services/api/user-courses.api";
import { userExamsApi } from "../../services/api/user-exams.api";
import { AddableCourse, CourseLite, CourseOption, CourseSummary } from "../../types/course.response";
import { MutationEnvelope } from "../../types/mutation.response";
import { CourseRequirementOverride } from "../../services/api/user-courses.api";
import { useCourseActions } from "../../hooks/useCourseActions";
import { usePoolSuggestedCourses } from "../../hooks/usePoolSuggestedCourses";
import { useHydratedCourses } from "../../hooks/useHydratedCourses";
import { programSlices } from "../../services/programSlices";
import { reconcileUserItems, STATUS_PRIORITY } from "../../services/userCourseCache";
import { ensureSliceCacheIdentity } from "../../services/sliceCacheIdentity";
import {
    ensureHydrated,
    getCatalogEntry,
    getCatalogVersion,
    subscribeCatalog,
} from "../../services/courseCatalog";
import { INITIAL_USER_STATE } from "../../utils/fulfillmentMerge";
import { getBackendCourseKey } from "../../utils/courseUtils";
import { toCourseSummary } from "../../utils/programData";
import { DEFAULT_SEMESTER } from "../../constants";
import { PoolProgressRow, CapNote } from "../program/OverallProgress";
import CourseRow from "../../components/course-row/CourseRow";
import { pickCountedCourses } from "../../utils/poolCountedCourses";
import { CourseRowSkeletonRows } from "../../components/loading/CourseRowSkeleton";
import ChevronIcon from "../../components/icons/ChevronIcon";

interface PoolProps {
    overlayProgramId?: string;
    overlayPoolKey?: string;
    onClose?: () => void;
    onDataChanged?: () => void;
    initialUserCourses?: CourseOption[];
}

/**
 * Pool(课程池)详情页(独立路由 + overlay 两用),数据来自切片编排器:
 * poolResults 随 skeleton 一起下发(skeleton 到 → pool 结构即时),成员展示数据
 * 优先取 userCourses,缺的走 /courses/batch 补水;推荐来自 pools/:poolKey/suggestions
 * (会话级缓存)。缓存命中(跨页返回 / overlay)时打开零网络请求。pool 对象每次
 * 渲染都取自最新 program 快照,改课后的轻量刷新自然带动本页更新。
 */
export default function Pool({ overlayProgramId, overlayPoolKey, onClose, onDataChanged, initialUserCourses }: PoolProps) {
    const isOverlay = !!onClose;

    const { programId: routeProgramId, poolKey: routePoolKey } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const programId = overlayProgramId ?? routeProgramId;
    const poolKey = overlayPoolKey ?? (routePoolKey ? decodeURIComponent(routePoolKey) : undefined);

    const backTo = (location.state as { from?: string } | null)?.from === 'home' ? '/' : `/programs/${programId}`;
    const [error, setError] = useState<string | undefined>(undefined);
    const [userCourses, setUserCourses] = useState<CourseOption[]>(initialUserCourses ?? []);
    // userCourses 是否已就绪:overlay 由宿主传入即为 true;独立路由待首拉完成。
    // 未就绪前不对成员课走 catalog 兜底(否则先出 INITIAL_USER_STATE 无日历图标的行)
    const [userCoursesLoaded, setUserCoursesLoaded] = useState<boolean>(initialUserCourses !== undefined);
    const [optimisticFavorites, setOptimisticFavorites] = useState<Map<string, boolean>>(new Map());
    const hasChangedRef = useRef(false);
    const hasInitialUserCoursesRef = useRef(initialUserCourses !== undefined);

    const { isLoggedIn, authLoading, user } = useAuth();
    const { openLoginModal } = useModal();

    const sliceVersion = useSyncExternalStore(programSlices.subscribe, programSlices.getVersion);
    void sliceVersion; // 订阅版本号驱动重渲染,快照在渲染期派生

    const snap = programId ? programSlices.getSnapshot(programId) : undefined;
    const program = snap?.status === 'loaded' ? snap.program : undefined;
    const pool = program?.poolResults.find(p => p.poolKey === poolKey);

    useEffect(() => {
        if (authLoading || !programId) return;
        ensureSliceCacheIdentity(user ? `user:${user.id}` : 'guest');
        programSlices.ensureProgram(programId);
    }, [authLoading, user, programId]);

    // userCourses:overlay 由宿主页传入(纯缓存,零请求);独立路由首次拉取
    useEffect(() => {
        if (authLoading) return;
        if (!isLoggedIn) {
            setUserCourses([]);
            setUserCoursesLoaded(true);
            return;
        }
        if (hasInitialUserCoursesRef.current) return;
        userCoursesApi.getCourses()
            .then(setUserCourses)
            .catch(err => {
                console.error('Error fetching user courses:', err);
            })
            .finally(() => setUserCoursesLoaded(true));
    }, [authLoading, isLoggedIn]);

    // stateVersion 落地前的乱序保护:只应用最新一次发起的刷新结果
    const refreshSeqRef = useRef(0);

    // 改课后的轻量刷新:fulfillment patch 打在编排器的合并对象上,
    // overlay 场景下宿主页(同一编排器)同步更新。
    const refreshFulfillment = async (outcome?: MutationEnvelope) => {
        if (!isLoggedIn || !programId) return;
        const seq = ++refreshSeqRef.current;
        try {
            const embedded = outcome?.fulfillments?.find(f => f.programId === programId);
            const affected = !outcome?.meta || outcome.meta.affectedProgramIds.includes(programId);
            const [courses, exams, fulfillment] = await Promise.all([
                userCoursesApi.getCourses(),
                userExamsApi.getExams(),
                embedded ?? (affected
                    ? programsApi.getProgramFulfillment(programId)
                    : Promise.resolve(null)),
            ]);
            if (seq !== refreshSeqRef.current) return; // 已有更新的刷新,丢弃
            const userItems = [...courses, ...exams];
            const reference = fulfillment ?? outcome?.fulfillments?.[0];
            if (reference) reconcileUserItems(reference.userStates, userItems);
            if (fulfillment) {
                programSlices.applyFulfillment(programId, fulfillment, userItems);
            }
            outcome?.fulfillments?.forEach(f => {
                if (f.programId !== programId) programSlices.applyFulfillment(f.programId, f, userItems);
            });
            setUserCourses(courses);
        } catch (err) {
            if (seq !== refreshSeqRef.current) return;
            console.error('Error refreshing fulfillment:', err);
            setError(err instanceof Error ? err.message : 'An error occurred');
        }
    };

    const handleBack = () => {
        if (isOverlay) {
            if (hasChangedRef.current) onDataChanged?.();
            onClose!();
        } else {
            navigate(backTo);
        }
    };

    const markChanged = () => { if (isOverlay) hasChangedRef.current = true; };

    const courseActions = useCourseActions({
        isLoggedIn,
        openLoginModal,
        userCourses,
        optimisticFavorites,
        setOptimisticFavorites,
        onSuccess: async (outcome) => {
            markChanged();
            await refreshFulfillment(outcome);
        },
        fulfillmentProgramIds: () => programSlices.getLoadedProgramIds(),
        onError: (err, action) => {
            if (action === 'favorite') {
                if (axios.isAxiosError(err) && err.response?.status === 401) {
                    setError('Please log in to save favorites');
                } else {
                    setError(err instanceof Error ? err.message : 'Failed to update favorite status');
                }
            } else {
                setError(err instanceof Error ? err.message : 'An error occurred');
            }
        },
        rethrow: true,
    });

    const handleAddCourse = async (
        course: AddableCourse,
        overrides: CourseRequirementOverride[],
        semester: string | null,
        classSectionIds?: number[],
    ) => {
        setError(undefined);
        await courseActions.handleAddCourse(course, overrides, semester, classSectionIds);
    };

    const handleDeleteCourse = async (course: CourseLite) => {
        setError(undefined);
        await courseActions.handleDeleteCourse(course);
    };

    const handleFavoriteCourse = courseActions.handleFavoriteCourse;

    // 兜底:后端尚未下发 memberCourseIds(部署顺序)时按空桶渲染,推荐区块照常
    const { completed = [], inProgress = [], planned = [], saved = [] } = pool?.memberCourseIds ?? {};
    const memberIds = useMemo(
        () => [...completed, ...inProgress, ...planned, ...saved],
        [completed, inProgress, planned, saved],
    );
    const memberMap = useMemberCourseMap(memberIds, userCourses, userCoursesLoaded);

    // 带 by 型 COURSE cap 的池(当前仅 cas_ge_pool):Completed 区只显示"真正计入"的那批,
    // 使门数与 constraint 的 applied 天然一致。挑选须跑增广路匹配(逐类贪心会挑少),
    // 见 utils/poolCountedCourses。无此类 cap 的池不受影响,照常显示全部 completed。
    const countedCompleted = useMemo(() => {
        const byCourseCap = pool?.contributionCaps.find(
            c => c.measure === "COURSE" && c.by && c.memberGroupValues,
        );
        const eligible = pool?.constraints.find(c => c.eligibleCourseIds)?.eligibleCourseIds;
        if (!byCourseCap?.memberGroupValues || !eligible) return completed;
        return pickCountedCourses({
            memberGroupValues: byCourseCap.memberGroupValues,
            eligibleCourseIds: eligible,
            capN: byCourseCap.n,
        });
    }, [pool?.contributionCaps, pool?.constraints, completed]);

    const { status: suggestStatus, courses: suggestedCourses, courseRequirements } =
        usePoolSuggestedCourses(programId ?? '', poolKey ?? '', userCourses);

    const allSummaries = useMemo(
        () => [...Array.from(memberMap.values()), ...suggestedCourses],
        [memberMap, suggestedCourses],
    );
    const hydrated = useHydratedCourses(allSummaries);

    const requirementNameById = useMemo(
        () => new Map((program?.requirements ?? []).map(r => [r.info.id, r.info.name])),
        [program?.requirements],
    );

    const renderCourseRow = (summary: CourseSummary) => (
        <CourseRow
            key={summary.mapKey}
            course={summary}
            hydrated={hydrated.get(summary.mapKey)}
            selectedSemester={DEFAULT_SEMESTER}
            onAddToSchedule={(course, classSectionIds) => handleAddCourse(course, [], DEFAULT_SEMESTER, classSectionIds)}
            onMarkAsTaken={(course) => handleAddCourse(course, [], null)}
            onDeleteFromSchedule={(course) => handleDeleteCourse(course)}
            onFavoriteToggle={(course) => handleFavoriteCourse(course)}
            optimisticFavorites={optimisticFavorites}
            userCourses={userCourses}
        />
    );

    const renderSection = (title: string, ids: string[]) => {
        if (ids.length === 0) return null;
        return (
            <>
                <tr>
                    <td colSpan={4} className="pt-6 pb-1">
                        <span className="text-base md:text-lg font-semibold text-gray-900">{title}</span>
                        <span className="ml-2 text-sm text-gray-400">{ids.length}</span>
                    </td>
                </tr>
                {ids.map(id => {
                    const summary = memberMap.get(id);
                    // 展示数据(标题等)未到位前出骨架行,补水到位后再显示完整课程行,
                    // 与 requirement section 加载 CourseRow 的口径一致(不闪课号裸行)
                    if (!summary) {
                        return <CourseRowSkeletonRows key={id} rows={1} />;
                    }
                    return renderCourseRow(summary);
                })}
            </>
        );
    };

    const skeletonError = snap?.status === 'error' ? (snap.error ?? 'Failed to load') : undefined;
    const notFound = program !== undefined && pool === undefined;

    if (!snap || snap.status === 'loading') {
        return (
            <div className="mt-2 relative">
                <div className="w-full max-w-[900px] mx-auto px-4 md:px-12 py-8 animate-pulse">
                    <div className="h-4 w-40 bg-gray-100 rounded mb-4" />
                    <div className="h-8 w-56 bg-gray-100 rounded mb-6" />
                    <div className="h-3 w-64 bg-gray-100 rounded mb-2" />
                    <table className="w-full border-collapse mt-4">
                        <tbody><CourseRowSkeletonRows rows={5} /></tbody>
                    </table>
                </div>
            </div>
        );
    }

    if (skeletonError || notFound) {
        return (
            <div className="mt-2 relative">
                <div className="w-full max-w-[900px] mx-auto px-4 md:px-12 pt-4">
                    <div className="mb-3">
                        <button
                            onClick={handleBack}
                            className="p-1.5 -ml-2 rounded-full hover:bg-gray-100 transition-colors"
                        >
                            <ChevronIcon direction="left" className="w-6 h-6 text-gray-600" />
                        </button>
                    </div>
                    <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {notFound ? 'Pool not found' : skeletonError}
                    </div>
                    {!notFound && (
                        <button
                            className="mt-3 text-sm text-red-700 underline"
                            onClick={() => programSlices.ensureProgram(programId!)}
                        >
                            Try again
                        </button>
                    )}
                </div>
            </div>
        );
    }

    const showSuggestions = suggestStatus === "loading"
        || (suggestStatus === "loaded" && suggestedCourses.length > 0);

    return (
        <div className="mt-2 relative">
            {program && pool && (
                <div className="w-full max-w-[900px] mx-auto px-4 md:px-12 py-8">
                    <div className="flex items-center gap-1 md:gap-2 mb-2">
                        <button
                            onClick={handleBack}
                            className="p-1.5 -ml-2 md:-ml-14 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0"
                        >
                            <ChevronIcon direction="left" className="w-6 h-6 md:w-7 md:h-7 text-gray-600" />
                        </button>
                        <div className="flex items-center gap-2">
                            <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                                <span className="text-[12px] font-bold text-gray-600">
                                    {program.info.id.substring(0, 2).toUpperCase()}
                                </span>
                            </div>
                            <span className="text-xs md:text-sm font-medium text-gray-700">
                                {program.info.name}
                            </span>
                        </div>
                    </div>
                    <h1 className="text-lg md:text-3xl font-bold text-gray-900 leading-tight">
                        {pool.constraints[0]?.label ?? "Pool Progress"}
                    </h1>

                    {error && (
                        <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                            {error}
                        </div>
                    )}

                    <div className="mt-5 max-w-[440px] flex flex-col gap-4">
                        {pool.constraints.map((c, i) => (
                            <PoolProgressRow
                                key={`c-${i}`}
                                label={c.label ?? "Requirement"}
                                applied={c.appliedUnitsCount}
                                required={c.requiredUnitsCount}
                                satisfied={c.satisfied}
                                needVerification={c.needVerification}
                                showLabel={pool.constraints.length > 1}
                            />
                        ))}
                    </div>
                    {pool.contributionCaps.length > 0 && (
                        <div className="mt-3 max-w-[440px] flex flex-col gap-1">
                            {pool.contributionCaps.map((cap, i) => (
                                <CapNote key={`cap-${i}`} cap={cap} />
                            ))}
                        </div>
                    )}
                    <table className="w-full border-collapse mt-4">
                        <tbody>
                            {renderSection("Completed", countedCompleted)}
                            {renderSection("In Progress", inProgress)}
                            {renderSection("Planned", planned)}
                            {renderSection("Saved", saved)}

                            {showSuggestions && (
                                <>
                                    <tr>
                                        <td colSpan={4} className="pt-6 pb-1">
                                            <span className="text-base md:text-lg font-semibold text-gray-900">Suggested courses</span>
                                        </td>
                                    </tr>
                                    {suggestStatus === "loading" ? (
                                        <CourseRowSkeletonRows rows={3} />
                                    ) : (
                                        suggestedCourses.map(summary => {
                                            const reqNames = (courseRequirements[summary.id] ?? [])
                                                .map(id => requirementNameById.get(id))
                                                .filter((name): name is string => !!name);
                                            return (
                                                <Fragment key={summary.mapKey}>
                                                    {renderCourseRow(summary)}
                                                    {reqNames.length > 0 && (
                                                        <tr>
                                                            <td colSpan={4} className="pb-2 text-xs text-gray-400">
                                                                Counts toward: {reqNames.join(", ")}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </Fragment>
                                            );
                                        })
                                    )}
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

/**
 * 成员课号 → CourseSummary。优先取 userCourses(同课多学期按状态优先级折叠,
 * 与后端分桶口径一致);不在其中的(跨端新增等)走 catalog 补水,合成初始态行。
 * 两边都没有的 id 不在返回 Map 中,调用方渲染课号占位行。
 */
function useMemberCourseMap(
    ids: string[],
    userCourses: CourseOption[] | undefined,
    userCoursesLoaded: boolean,
): Map<string, CourseSummary> {
    const catalogVersion = useSyncExternalStore(subscribeCatalog, getCatalogVersion);
    const idsSignature = ids.join(" ");

    useEffect(() => {
        // 成员课的状态(日历图标)只能来自 userCourses,未就绪前不补水/不兜底,先出骨架
        if (!userCoursesLoaded) return;
        const ownedIds = new Set((userCourses ?? []).map(c => c.id));
        const missing = ids.filter(id => !ownedIds.has(id));
        if (missing.length > 0) {
            ensureHydrated(missing).catch(err => {
                console.error("Error hydrating pool member courses:", err);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idsSignature, userCourses, userCoursesLoaded]);

    return useMemo(() => {
        void catalogVersion; // catalog 缓存变化时重新合成
        const ownedById = new Map<string, CourseOption>();
        for (const course of userCourses ?? []) {
            const prev = ownedById.get(course.id);
            if (!prev || STATUS_PRIORITY[course.userState.status] > STATUS_PRIORITY[prev.userState.status]) {
                ownedById.set(course.id, course);
            }
        }
        const result = new Map<string, CourseSummary>();
        for (const id of ids) {
            const own = ownedById.get(id);
            if (own) {
                result.set(id, toCourseSummary(own, getBackendCourseKey(own)));
                continue;
            }
            // userCourses 未就绪前不落 catalog 兜底,避免先渲染缺日历图标的行(留空 → 骨架)
            if (!userCoursesLoaded) continue;
            const catalog = getCatalogEntry(id);
            if (catalog) {
                result.set(id, toCourseSummary({ ...catalog, userItemId: null, userState: INITIAL_USER_STATE }, id));
            }
        }
        return result;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idsSignature, userCourses, catalogVersion, userCoursesLoaded]);
}
